import React, { Fragment } from 'react';
import { NavItem, NavLink, Button } from 'reactstrap';
import classNames from 'classnames';

import { Link } from 'site-modules/shared/components/link/link';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';

const getNavBarData = entriesToRender =>
  !!entriesToRender && entriesToRender.childEntries
    ? entriesToRender.childEntries.filter(entry => entry.id === 'navigation').pop().childEntries
    : [];

const getNavChildEl = (mainNavEntry, onClick) => (
  <NavItem key={mainNavEntry.id}>
    <NavLink
      className="size-16 fw-normal p-0"
      href="#"
      onClick={onClick}
      data-tracking-id="nav_menu_item"
      data-tracking-value={mainNavEntry.contentMetadata.title}
    >
      <span id={mainNavEntry.id} className="d-inline-block">
        {mainNavEntry.contentMetadata.title}
        <hr className="m-0" />
      </span>
    </NavLink>
  </NavItem>
);

const getNavEntryChildren = (mainNavEntry, isSmall = false) => (
  <div className={classNames('collapse-container', { 'd-flex': !isSmall, 'py-1_5': !isSmall })}>
    {mainNavEntry.childEntries.map((subNavEntry, idx) => (
      <div
        className={classNames('text-cool-gray-30', {
          'py-0': isSmall,
          'collapse-content': !isSmall,
          'm-0': !isSmall,
          'ps-0_75': !isSmall && idx === 0,
          'pe-1_5': !isSmall && idx === 0,
          'ps-1_5': !isSmall && idx > 0,
          'pe-0_75': !isSmall && idx > 0,
        })}
        key={subNavEntry.id}
      >
        {subNavEntry.contentMetadata.title && (
          <h4
            className={classNames('text-blue-30 fw-bold', {
              'heading-4': isSmall,
              'm-0 py-1': isSmall,
              'mb-1_5': !isSmall,
              'size-16': !isSmall,
            })}
          >
            {subNavEntry.contentMetadata.title}
          </h4>
        )}
        {subNavEntry.links &&
          subNavEntry.links.map((subNavLink, linkIdx) => (
            <div
              key={subNavLink.title}
              className={classNames('m-0', {
                'ps-1 pb-0_5': isSmall,
                'mt-1_5': !isSmall && linkIdx > 0,
              })}
            >
              <Link
                to={subNavLink.href}
                className={classNames('text-cool-gray-30 text-decoration-none', {
                  'd-block py-0_5': isSmall,
                })}
                data-tracking-id="nav_menu_item"
                data-tracking-value={subNavLink.title}
              >
                {subNavLink.title}
              </Link>
            </div>
          ))}
        {isSmall && <hr className={classNames('m-0', { 'mb-1': idx + 1 < mainNavEntry.childEntries.length })} />}
      </div>
    ))}
  </div>
);

const getNavChildEntries = (mainNavEntry, onClick, isSmall = false) => ({
  id: mainNavEntry.id,
  el: isSmall ? getNavEntryChildren(mainNavEntry, isSmall) : getNavChildEl(mainNavEntry, onClick),
  children: isSmall ? null : getNavEntryChildren(mainNavEntry, isSmall),
});

const getNavButtonEl = mainNavEntry => (
  <NavItem key={mainNavEntry.id}>
    <Button
      className="size-16 fw-normal text-decoration-none m-0 p-0"
      color="link"
      href={mainNavEntry.contentMetadata.url}
      data-tracking-id="nav_menu_item"
      data-tracking-value={mainNavEntry.contentMetadata.title}
    >
      <ContentFragment componentToUse="span" id={mainNavEntry.id}>
        {mainNavEntry.contentMetadata.title}
      </ContentFragment>
    </Button>
  </NavItem>
);

const getNavButton = mainNavEntry => ({
  id: mainNavEntry.id,
  el: getNavButtonEl(mainNavEntry),
});

const getNavEl = (mainNavEntry, isSmall = false) => (
  <Fragment>
    <NavItem key={mainNavEntry.id}>
      <NavLink
        className={classNames({
          'size-16': !isSmall,
          'p-0': !isSmall,
          'fw-normal': !isSmall,
          'heading-4': isSmall,
          'px-0': isSmall,
          'py-1': isSmall,
          'fw-bold': isSmall,
        })}
        href={mainNavEntry.contentMetadata.url}
        data-tracking-id="nav_menu_item"
        data-tracking-value={mainNavEntry.contentMetadata.title}
      >
        {isSmall ? (
          <ContentFragment id={mainNavEntry.id}>{mainNavEntry.contentMetadata.title}</ContentFragment>
        ) : (
          <span id={mainNavEntry.id} className="d-inline-block">
            {mainNavEntry.contentMetadata.title}
            <hr className="m-0" />
          </span>
        )}
      </NavLink>
    </NavItem>
    {isSmall && <hr className="m-0" />}
  </Fragment>
);

const getNavEntry = (mainNavEntry, isSmall = false) => ({
  id: mainNavEntry.id,
  el: getNavEl(mainNavEntry, isSmall),
});

export const renderNavEntries = (entriesToRender, onClick) => {
  const navBarData = getNavBarData(entriesToRender);
  if (!navBarData.length) {
    return [{ el: null }];
  }

  return navBarData
    .reduce((entries, mainNavEntry) => {
      if ('childEntries' in mainNavEntry) {
        entries.push(getNavChildEntries(mainNavEntry, onClick));
      } else if ('button' in mainNavEntry.contentMetadata && mainNavEntry.contentMetadata.button === 'true') {
        entries.push(getNavButton(mainNavEntry));
      } else {
        entries.push(getNavEntry(mainNavEntry));
      }
      return entries;
    }, [])
    .slice();
};

export const getContactData = globalNavData => {
  const contactEntry =
    !!globalNavData && globalNavData.childEntries && globalNavData.childEntries.find(entry => entry.id === 'contact');
  return Object.assign(
    { title: 'Contact Us', url: '/industry/contact-us.html' },
    (contactEntry && contactEntry.contentMetadata) || {}
  );
};

export function renderNavBarEntries(entriesToRender, onClick) {
  const navBarData = getNavBarData(entriesToRender);
  if (!navBarData.length) {
    return [{ el: null }];
  }

  return navBarData
    .reduce((entries, mainNavEntry) => {
      if ('childEntries' in mainNavEntry) {
        entries.push(getNavChildEntries(mainNavEntry, onClick, true));
      } else {
        entries.push(getNavEntry(mainNavEntry, true));
      }
      return entries;
    }, [])
    .slice();
}
